import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LoginBranding } from '../../config';
import { useTheme } from '../../theme';
import Button from '../Button';
import Card from '../Card';
import Input from '../Input';
import { LanguageSelector } from '../LanguageSelector';
import { Toast } from '../Toast';

export interface LoginFormValues {
  email: string;
  password: string;
}

export interface LoginProps {
  /** Config de marca: fixa (B2B) ou whitelabel (B2B2C) — mesmo componente */
  branding: LoginBranding;
  /**
   * Chamado ao enviar o formulário. Pode ser síncrono ou assíncrono.
   * Se receber credentials, deve chamar a API de login; em 401 rejeite para exibir toast "E-mail ou senha incorretos".
   */
  onLogin?: (credentials: {
    email: string;
    password: string;
  }) => void | Promise<void>;
  /**
   * Navegação para a tela Esqueci a senha.
   * Recebe o e-mail digitado para permitir deep link (ex.: prefill no forgot-password).
   */
  onForgotPassword?: (email: string) => void;
}

export default function Login({
  branding,
  onLogin,
  onForgotPassword,
}: LoginProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const primaryColor = branding.primaryColor;
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!loginError) return;
    const timer = window.setTimeout(() => setLoginError(null), 5000);
    return () => window.clearTimeout(timer);
  }, [loginError]);

  const { control, handleSubmit: handleFormSubmit, getValues } =
    useForm<LoginFormValues>({
      defaultValues: { email: '', password: '' },
      mode: 'onSubmit',
    });

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.gradient.page,
    padding: theme.spacing.lg,
    position: 'relative',
  };

  const languageSelectorWrapperStyle: React.CSSProperties = {
    position: 'absolute',
    top: theme.spacing['3xl'],
    right: theme.spacing.md,
  };

  const cardContentStyle: React.CSSProperties = {
    width: theme.layout.cardWidth,
    minHeight: theme.layout.cardHeight,
    borderRadius: theme.shape.borderRadius.lg,
  };

  const formStyle: React.CSSProperties = {
    width: '100%',
    textAlign: 'left',
  };

  const logoStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamilySerif,
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.palette.text.primary,
    letterSpacing: '0.02em',
    marginBottom: theme.spacing.xs,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing.xs,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.typography.fontSize.md,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing.lg,
  };

  const forgotLinkStyle: React.CSSProperties = {
    display: 'inline-block',
    textAlign: 'center',
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    color: primaryColor,
    textDecoration: 'none',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    marginBottom: theme.spacing.lg,
    fontFamily: 'inherit',
  };

  const footerStyle: React.CSSProperties = {
    marginTop: theme.spacing.lg,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.normal,
    color: theme.palette.text.white,
    opacity: 0.8,
  };

  const onSubmit = async (values: LoginFormValues) => {
    setLoginError(null);
    if (!onLogin) return;
    setIsSubmitting(true);
    try {
      const result = onLogin({
        email: values.email,
        password: values.password,
      });
      if (result instanceof Promise) {
        await result;
      }
    } catch (error) {
      // Exibe a mensagem retornada pela API (ex.: validação de senha); senão mensagem genérica
      const message =
        error instanceof Error && error.message
          ? error.message
          : t('login.invalid_credentials');
      setLoginError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={pageStyle} data-e2e="login-page">
      <div style={languageSelectorWrapperStyle}>
        <LanguageSelector />
      </div>
      <Card style={cardContentStyle}>
        <div>
          <div style={logoStyle}>
            {typeof branding.logo !== 'string' || !branding.logo ? (
              branding.logo
            ) : (
              <img
                src={branding.logo}
                alt={t('login.logo_alt')}
                style={{
                  width: theme.layout.logoWidth,
                  height: theme.layout.logoHeight,
                }}
              />
            )}
          </div>
          <h1 style={titleStyle}>{branding.title}</h1>
          <p style={subtitleStyle}>{branding.subtitle}</p>
        </div>

        <form
          style={formStyle}
          onSubmit={handleFormSubmit(onSubmit)}
          data-e2e="login-form"
        >
          {loginError && (
            <Toast
              message={loginError}
              variant="error"
              role="alert"
              data-e2e="login-error"
            />
          )}
          <Controller
            name="email"
            control={control}
            rules={{
              required: t('login.validation_required'),
              validate: (value: string) =>
                (value?.trim()?.length ?? 0) > 0 ||
                t('login.validation_required'),
            }}
            render={({
              field,
              fieldState,
            }: {
              field: {
                value: string;
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
                onBlur: () => void;
              };
              fieldState: { error?: { message?: string } };
            }) => (
              <Input
                id="login-email"
                titulo={t('login.email_label')}
                type="email"
                placeholder={t('login.email_placeholder')}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                data-e2e="login-email"
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={{
              required: t('login.validation_required'),
              validate: (value: string) =>
                (value?.trim()?.length ?? 0) > 0 ||
                t('login.validation_required'),
            }}
            render={({
              field,
              fieldState,
            }: {
              field: {
                value: string;
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
                onBlur: () => void;
              };
              fieldState: { error?: { message?: string } };
            }) => (
              <Input
                id="login-password"
                titulo={t('login.password_label')}
                type="password"
                showPasswordToggle
                passwordToggleAriaLabelShow={t('forgot_password.show_password')}
                passwordToggleAriaLabelHide={t('forgot_password.hide_password')}
                placeholder={t('login.password_placeholder')}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                data-e2e="login-password"
              />
            )}
          />
          <Button
            type="submit"
            size="large"
            style={{ width: '100%' }}
            backgroundColor={primaryColor}
            disabled={isSubmitting}
            data-e2e="login-submit"
          >
            {isSubmitting ? t('login.submitting') : t('login.submit')}
          </Button>
          <div
            style={{
              textAlign: 'center',
              width: '100%',
              marginTop: theme.spacing.md,
            }}
          >
            <button
              type="button"
              style={forgotLinkStyle}
              onClick={() => {
                const email = (getValues('email') ?? '').trim();
                onForgotPassword?.(email);
              }}
              data-e2e="login-forgot-link"
            >
              {t('login.forgot_password')}
            </button>
          </div>
        </form>
      </Card>
      <footer style={footerStyle}>{branding.footerText}</footer>
    </div>
  );
}
